import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";
import { useCellRender } from "../../../hooks/useCellRender";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import {
  IconCoins,
  IconWallet,
  IconReceipt,
  IconList,
  IconPlayerPause,
  IconCircleCheck,
  IconTransferIn,
  IconTransferOut,
} from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Tabs,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Grid,
  Card,
  Tooltip,
  ActionIcon,
  Group,
  Stack,
  Modal,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import PayrollBidRowHeader from "./PayrollBidRowHeader";
import PayrollBidRowContent from "./PayrollBidRowContent";
import ByOrderRowHeader from "./ByOrderRowHeader";
import ByOrderRowContent from "./ByOrderRowContent";
import ByIncentiveRowHeader from "./ByIncentiveRowHeader";
import ByIncentiveRowContent from "./ByIncentiveRowContent";
import ConfirmationTabContent from "./ConfirmationTabContent";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";
import PayrollBidsVerification from "./PayrollBidsVerification";
import ReleasedFromPoolTabContent from "./ReleasedFromPoolTabContent";
import ByOrderFilterBar from "./ByOrderFilterBar";
import EntityTabAnchor from "./EntityTabAnchor";
import {
  applyByOrderFilters,
  getBatchHoldAction,
} from "./byOrderFilterUtils";
import {
  getPayrollSourceIdForApi,
} from "./payrollSourceHelpers";

const PayrollBidsTab = ({ form }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [cellRender] = useCellRender();

  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totalBids, setTotalBids] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [allBidsPage, setAllBidsPage] = useState(1);
  const [allBidsTotal, setAllBidsTotal] = useState(0);
  const [allBidsLimit] = useState(50);
  const [walletBidsMap, setWalletBidsMap] = useState({});
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [loadingWalletId, setLoadingWalletId] = useState(null);
  // By Orders / By Incentive — lazy load
  const [orderSummary, setOrderSummary] = useState([]);
  const [incentiveSummary, setIncentiveSummary] = useState([]);
  const [orderBidsMap, setOrderBidsMap] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loadingSourceKey, setLoadingSourceKey] = useState(null);
  const [orderGroupCount, setOrderGroupCount] = useState(0);
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(false);
  const [incentiveGroupCount, setIncentiveGroupCount] = useState(0);
  const [releasedBidCount, setReleasedBidCount] = useState(0);
  const [releasedBids, setReleasedBids] = useState([]);
  const [releasedTotalAmount, setReleasedTotalAmount] = useState(0);
  const [releasedLoading, setReleasedLoading] = useState(false);
  const [releasedFromBidCount, setReleasedFromBidCount] = useState(0);
  const [releasedFromBids, setReleasedFromBids] = useState([]);
  const [releasedFromTotalAmount, setReleasedFromTotalAmount] = useState(0);
  const [releasedFromLoading, setReleasedFromLoading] = useState(false);
  const [innerTab, setInnerTab] = useState("byWallets");
  const [verification, setVerification] = useState(null);
  const [negativeBalancePreview, setNegativeBalancePreview] = useState([]);
  const [orderTableFilterQuery, setOrderTableFilterQuery] = useState({});
  const [duplicateConflicts, setDuplicateConflicts] = useState([]);
  const [pendingBatchHold, setPendingBatchHold] = useState(null);
  const [
    batchHoldModalOpened,
    { open: openBatchHoldModal, close: closeBatchHoldModal },
  ] = useDisclosure(false);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const payrollId = form?.values?._id;

  const filteredOrderSummary = useMemo(
    () => applyByOrderFilters(orderSummary, orderTableFilterQuery),
    [orderSummary, orderTableFilterQuery],
  );

  const batchHoldAction = useMemo(
    () => getBatchHoldAction(filteredOrderSummary),
    [filteredOrderSummary],
  );

  const applySummaryToState = (data) => {
    setSummary(data.summary || []);
    const total = data.totalBids || 0;
    setTotalBids(total);
    setTotalAmount(data.totalAmount || 0);
    setAllBidsTotal(total);
    setOrderGroupCount(data.orderGroupCount ?? 0);
    setIncentiveGroupCount(data.incentiveGroupCount ?? 0);
    form.setFieldValue("payrollBidsCount", total);
    form.setFieldValue("pendingCount", data.pendingCount || 0);
    form.setFieldValue("totalPendingAmount", data.totalPendingAmount || 0);
    form.setFieldValue("holdCount", data.holdCount || 0);
    form.setFieldValue("totalHoldAmount", data.totalHoldAmount || 0);
    form.setFieldValue("settledCount", data.settledCount || 0);
    form.setFieldValue("totalSettledAmount", data.totalSettledAmount || 0);
    setVerification(data.verification || null);
    setReleasedBidCount(data.verification?.releasedBidCount ?? 0);
    setNegativeBalancePreview(data.negativeBalancePreview || []);
  };

  const reloadBids = async () => {
    if (!payrollId) return;
    // Save scroll position before reload
    const scrollPos = window.scrollY;
    try {
      setLoading(true);
      const result = await api.Payroll2.getPayrollBidsSummary(payrollId);
      if (result && result.success && result.data) {
        applySummaryToState(result.data);
      }
      if (innerTab === "allBids" && allBidsTotal > 0) {
        const pageResult = await api.Payroll2.getPayrollBids(payrollId, {
          page: allBidsPage,
          limit: allBidsLimit,
        });
        if (pageResult?.success && pageResult.data) {
          setBids(pageResult.data.bids || []);
          setAllBidsTotal(pageResult.data.total ?? 0);
        }
      }
      if (expandedWallet) {
        const wResult = await api.Payroll2.getPayrollBids(payrollId, {
          walletId: expandedWallet,
          limit: 500,
        });
        if (wResult?.success && wResult.data?.bids) {
          setWalletBidsMap((prev) => ({
            ...prev,
            [expandedWallet]: wResult.data.bids,
          }));
        }
      }
      if (innerTab === "byOrder" || innerTab === "byIncentive") {
        setOrderSummary([]);
        setIncentiveSummary([]);
        setOrderBidsMap({});
        await loadOrderGroupSummary(true); // Force reload
        // Reload bids for expanded order if one is open
        if (expandedOrder) {
          const item = orderSummary.find((g) => g.key === expandedOrder);
          if (item) {
            const sourceId = getPayrollSourceIdForApi(item);
            await loadSourceBids(expandedOrder, sourceId, item.sourceType, true);
          }
        }
      }
      if (innerTab === "releasedFromPool") {
        await loadReleasedBids(true);
      }
      if (innerTab === "releasedFromPayroll") {
        await loadReleasedFromBids(true);
      }
    } catch (error) {
      console.log("reloadBids error:", error);
    } finally {
      setLoading(false);
      // Restore scroll position after reload
      setTimeout(() => window.scrollTo(0, scrollPos), 0);
    }
  };

  const loadDuplicateConflicts = async () => {
    if (!payrollId) {
      setDuplicateConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(payrollId);
      if (result?.success) {
        setDuplicateConflicts(
          result.data?.excludedConflicts || result.data?.conflicts || [],
        );
      }
    } catch (error) {
      console.log("loadDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    setOrderSummary([]);
    setIncentiveSummary([]);
    setOrderBidsMap({});
    setOrderTableFilterQuery({});

    const loadSummary = async () => {
      if (!payrollId) return;
      try {
        setLoading(true);
        const result = await api.Payroll2.getPayrollBidsSummary(payrollId);
        if (result && result.success && result.data) {
          applySummaryToState(result.data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    loadSummary();
    loadReleasedFromBids();
    loadDuplicateConflicts();
  }, [payrollId]);

  const loadAllBidsPage = async (page = 1) => {
    if (!payrollId) return;
    try {
      setLoading(true);
      const result = await api.Payroll2.getPayrollBids(payrollId, {
        page,
        limit: allBidsLimit,
      });
      if (result?.success && result.data) {
        setBids(result.data.bids || []);
        setAllBidsTotal(result.data.total ?? 0);
        setAllBidsPage(page);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBids = async (walletId) => {
    if (!payrollId || !walletId || walletBidsMap[walletId]) return;
    try {
      setLoadingWalletId(walletId);
      const result = await api.Payroll2.getPayrollBids(payrollId, {
        walletId,
        limit: 100000,
      });
      if (result?.success && result.data?.bids) {
        setWalletBidsMap((prev) => ({ ...prev, [walletId]: result.data.bids }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingWalletId(null);
    }
  };

  const loadOrderGroupSummary = async (forceReload = false) => {
    if (!payrollId) return;
    // Only skip loading if we already have data AND not forcing reload
    if (!forceReload && (orderSummary.length > 0 || incentiveSummary.length > 0))
      return;
    try {
      setOrderSummaryLoading(true);
      const pageSize = 150;
      let page = 1;
      let allOrders = [];
      let allIncentives = [];
      let orderTotal = 0;
      let loadFailed = false;

      do {
        const result = await api.Payroll2.getOrderGroupSummary(payrollId, {
          page,
          limit: pageSize,
          includeIncentives: page === 1,
        });
        if (!result?.success || !result.data) {
          loadFailed = true;
          break;
        }
        const orders = result.data.orderGroups || [];
        orderTotal = result.data.orderGroupTotal ?? orders.length;
        allOrders = allOrders.concat(orders);
        if (page === 1) {
          allIncentives = result.data.incentiveGroups || [];
        }
        page += 1;
      } while (allOrders.length < orderTotal);

      if (!loadFailed) {
        setOrderSummary(allOrders);
        setIncentiveSummary(allIncentives);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setOrderSummaryLoading(false);
    }
  };

  const loadSourceBids = async (key, sourceId, sourceType, forceReload = false) => {
    if (!payrollId || !sourceId) return;
    if (!forceReload && orderBidsMap[key]) return;
    try {
      setLoadingSourceKey(key);
      const result = await api.Payroll2.getPayrollBids(payrollId, {
        sourceId,
        sourceType,
      });
      if (result?.success && result.data?.bids) {
        setOrderBidsMap((prev) => ({ ...prev, [key]: result.data.bids }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingSourceKey(null);
    }
  };

  const loadReleasedBids = async (forceReload = false) => {
    if (!payrollId) return;
    if (!forceReload && releasedBids.length > 0) return;
    try {
      setReleasedLoading(true);
      const result = await api.Payroll2.getReleasedHoldingBids(payrollId);
      if (result?.success && result.data) {
        setReleasedBids(result.data.bids || []);
        setReleasedTotalAmount(result.data.totalAmount || 0);
        if (result.data.totalBids != null) {
          setReleasedBidCount(result.data.totalBids);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setReleasedLoading(false);
    }
  };

  const loadReleasedFromBids = async (forceReload = false) => {
    if (!payrollId) return;
    if (!forceReload && releasedFromBids.length > 0) return;
    try {
      setReleasedFromLoading(true);
      const result = await api.Payroll2.getReleasedFromPayrollBids(payrollId);
      if (result?.success && result.data) {
        setReleasedFromBids(result.data.bids || []);
        setReleasedFromTotalAmount(result.data.totalAmount || 0);
        if (result.data.totalBids != null) {
          setReleasedFromBidCount(result.data.totalBids);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setReleasedFromLoading(false);
    }
  };

  const handleInnerTabChange = (tab) => {
    setInnerTab(tab);
    if (tab === "allBids" && bids.length === 0 && totalBids > 0) {
      loadAllBidsPage(1);
    }
    if (tab === "byOrder" || tab === "byIncentive") {
      loadOrderGroupSummary();
    }
    if (tab === "releasedFromPool") {
      loadReleasedBids();
    }
    if (tab === "releasedFromPayroll") {
      loadReleasedFromBids();
    }
  };

  const handleWalletAccordionChange = (value) => {
    setExpandedWallet(value);
    if (value) loadWalletBids(value);
  };

  const handleOrderAccordionChange = (key) => {
    setExpandedOrder(key);
    if (!key) return;
    const item = orderSummary.find((g) => g.key === key);
    if (item) {
      const sourceId = getPayrollSourceIdForApi(item);
      loadSourceBids(key, sourceId, item.sourceType);
    }
  };

  const handleIncentiveAccordionChange = (key) => {
    if (!key) return;
    const item = incentiveSummary.find((g) => g.key === key);
    if (item) {
      const sourceId = item.incentive?._id || item.key;
      loadSourceBids(key, sourceId, "INCENTIVE");
    }
  };

  const handleFetchBids = async () => {
    if (!payrollId) return;

    try {
      setLoading(true);
      const result = await api.Payroll2.generatePayrollBids(payrollId);

      if (result && result.success) {
        const carryRelease = result.data?.carryRelease;
        const skippedCount = result.data?.skippedCount || 0;
        const createdCount = result.data?.count ?? 0;
        if (carryRelease?.releasedCount > 0) {
          showNotification({
            title: t("payroll2.bids.carryForwardImportedTitle"),
            color: "blue",
            message: t("payroll2.bids.carryForwardImported", {
              count: carryRelease.releasedCount,
              amount: formatter.currency(carryRelease.releasedAmount || 0),
            }),
            autoClose: 8000,
          });
        }
        if (skippedCount > 0) {
          showNotification({
            title: "Duplicate sources skipped",
            color: "orange",
            message: `${skippedCount} source(s) already commissioned on dispatched payrolls were skipped. Bids generated for the remaining sources.`,
            autoClose: 10000,
          });
        }
        if (createdCount === 0) {
          showNotification({
            title: "No bids generated",
            color: "yellow",
            message:
              "No commission sources are ready. Run Client Payments Search, then Orders Update, before generating bids.",
            autoClose: 12000,
          });
        }
        setOrderSummary([]);
        setIncentiveSummary([]);
        setOrderBidsMap({});
        setWalletBidsMap({});
        setOrderTableFilterQuery({});
        await reloadBids();
        await loadDuplicateConflicts();
        if (form?.reload) {
          await form.reload();
        }
      } else if (result?.error?.conflicts?.length) {
        setDuplicateConflicts(result.error.conflicts);
        showNotification({
          title: "Generate bids failed",
          message:
            result.error.message ||
            result.message ||
            "Could not generate payroll bids.",
          color: "red",
          autoClose: 10000,
        });
      } else if (result?.message) {
        showNotification({
          title: "Generate bids failed",
          message: result.message,
          color: "red",
        });
      }
    } catch (error) {
      console.log("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldByWallet = async (walletId, hold) => {
    if (!form?.values?._id || !walletId) return;
    try {
      setLoading(true);
      await api.Payroll2.holdByWallet(form.values._id, walletId, hold);
      await reloadBids();
    } catch (error) {
      console.log("holdByWallet error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldBySource = async (sourceId, sourceType, hold) => {
    if (!form?.values?._id || !sourceId) return;
    try {
      setLoading(true);
      await api.Payroll2.holdBySource(
        form.values._id,
        sourceId,
        sourceType,
        hold,
      );
      await reloadBids();
    } catch (error) {
      console.log("holdBySource error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBidHold = async (bidId, currentStatus) => {
    if (!bidId) return;
    try {
      setLoading(true);
      const newStatus = currentStatus === "HOLD" ? "PENDING" : "HOLD";
      await api.Payroll2.updateBidStatus(bidId, newStatus);
      await reloadBids();
    } catch (error) {
      console.log("toggleBidHold error:", error);
    } finally {
      setLoading(false);
    }
  };

  const onOrderTableFilterChange = useCallback(
    (field, values, possibleValues) => {
      if (!field) return;
      const newFilterQuery = { ...orderTableFilterQuery };

      if (values.length === 0) {
        newFilterQuery[field] = null;
        setOrderTableFilterQuery(newFilterQuery);
        return;
      }

      if (
        values &&
        possibleValues &&
        values.length === possibleValues.length &&
        newFilterQuery[field]
      ) {
        delete newFilterQuery[field];
        setOrderTableFilterQuery(newFilterQuery);
        return;
      }

      newFilterQuery[field] = { $in: values };
      setOrderTableFilterQuery(newFilterQuery);
    },
    [orderTableFilterQuery],
  );

  const executeBatchHold = async (hold) => {
    const sources = filteredOrderSummary
      .filter((g) => (g.bidCount || 0) > 0)
      .map((item) => ({
        sourceId: getPayrollSourceIdForApi(item),
        sourceType: item.sourceType,
      }))
      .filter((s) => s.sourceId);

    if (!sources.length || !payrollId) return;

    try {
      setLoading(true);
      await api.Payroll2.holdBySources(payrollId, sources, hold);
      await reloadBids();
    } catch (error) {
      console.log("batch hold error:", error);
    } finally {
      setLoading(false);
      closeBatchHoldModal();
      setPendingBatchHold(null);
    }
  };

  const handleBatchHoldClick = () => {
    const { action, count } = batchHoldAction;
    if (!action || count === 0 || isConfirmed) return;

    const hold = action === "hold";
    if (count > 10) {
      setPendingBatchHold(hold);
      openBatchHoldModal();
      return;
    }
    executeBatchHold(hold);
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Group position="apart">
          <Box>
            <Title order={5}>Payroll Bids</Title>
            <Text size="xs" color="dimmed" mt="xs">
              Generate commission bids from DN and Orders based on commission
              settings
            </Text>
          </Box>
          <Button
            size="xs"
            leftIcon={<IconCoins size={14} />}
            onClick={handleFetchBids}
            color="teal"
            disabled={isConfirmed}
            loading={loading}
          >
            Generate Bids
          </Button>
        </Group>
        {duplicateConflicts.length > 0 && (
          <Box mt="md">
            <DuplicateBidConflictsAlert conflicts={duplicateConflicts} />
            <Text size="xs" color="dimmed" mt="xs">
              Generate Bids will skip these sources and commission the rest.
            </Text>
          </Box>
        )}
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      {totalBids > 0 &&
        (() => {
          const pendingCount = form?.values?.pendingCount ?? 0;
          const holdCount = form?.values?.holdCount ?? 0;
          const settledCount = form?.values?.settledCount ?? 0;
          const pendingAmount = form?.values?.totalPendingAmount ?? 0;
          const holdAmount = form?.values?.totalHoldAmount ?? 0;
          const settledAmount = form?.values?.totalSettledAmount ?? 0;

          return (
            <>
              <PayrollBidsVerification
                verification={verification}
                formatter={formatter}
              />
            <Grid mb="lg" gutter="md">
              <Grid.Col span={3}>
                <Paper p="md" withBorder>
                  <Text size="xs" color="dimmed" mb={4}>
                    Total Bids
                  </Text>
                  <Group position="apart" align="flex-end">
                    <Text size="xl" weight={700}>
                      {totalBids}
                    </Text>
                    <Text
                      size="md"
                      weight={600}
                      color={totalAmount >= 0 ? "green" : "red"}
                    >
                      {formatter.currency(totalAmount)}
                    </Text>
                  </Group>
                </Paper>
              </Grid.Col>

              <Grid.Col span={3}>
                <Paper
                  p="md"
                  withBorder
                  style={{ borderLeft: "3px solid #868e96" }}
                >
                  <Group position="apart" align="center" mb={4}>
                    <Text size="xs" color="dimmed">
                      Pending
                    </Text>
                    <Badge size="sm" color="gray" variant="filled">
                      PENDING
                    </Badge>
                  </Group>
                  <Group position="apart" align="flex-end">
                    <Text size="xl" weight={700}>
                      {pendingCount}
                    </Text>
                    <Text
                      size="md"
                      weight={600}
                      color={pendingAmount >= 0 ? "green" : "red"}
                    >
                      {formatter.currency(pendingAmount)}
                    </Text>
                  </Group>
                </Paper>
              </Grid.Col>

              <Grid.Col span={3}>
                <Paper
                  p="md"
                  withBorder
                  style={{ borderLeft: "3px solid #fab005" }}
                >
                  <Group position="apart" align="center" mb={4}>
                    <Text size="xs" color="dimmed">
                      On Hold
                    </Text>
                    <Badge size="sm" color="yellow" variant="filled">
                      HOLD
                    </Badge>
                  </Group>
                  <Group position="apart" align="flex-end">
                    <Text size="xl" weight={700} color="yellow">
                      {holdCount}
                    </Text>
                    <Text size="md" weight={600} color="yellow">
                      {formatter.currency(holdAmount)}
                    </Text>
                  </Group>
                </Paper>
              </Grid.Col>

              <Grid.Col span={3}>
                <Paper
                  p="md"
                  withBorder
                  style={{ borderLeft: "3px solid #40c057" }}
                >
                  <Group position="apart" align="center" mb={4}>
                    <Text size="xs" color="dimmed">
                      Settled
                    </Text>
                    <Badge size="sm" color="green" variant="filled">
                      SETTLED
                    </Badge>
                  </Group>
                  <Group position="apart" align="flex-end">
                    <Text size="xl" weight={700} color="green">
                      {settledCount}
                    </Text>
                    <Text size="md" weight={600} color="green">
                      {formatter.currency(settledAmount)}
                    </Text>
                  </Group>
                </Paper>
              </Grid.Col>
            </Grid>
            </>
          );
        })()}

      {totalBids > 0 || releasedBidCount > 0 || releasedFromBidCount > 0 ? (
            <Tabs value={innerTab} onTabChange={handleInnerTabChange}>
              <Tabs.List mb="md">
                <Tabs.Tab value="byWallets" icon={<IconWallet size={14} />}>
                  By Wallets ({summary.length})
                </Tabs.Tab>
                <Tabs.Tab value="byOrder" icon={<IconReceipt size={14} />}>
                  By Orders ({orderGroupCount})
                </Tabs.Tab>
                <Tabs.Tab value="byIncentive" icon={<IconCoins size={14} />}>
                  By Incentive ({incentiveGroupCount})
                </Tabs.Tab>
                <Tabs.Tab
                  value="releasedFromPool"
                  icon={<IconTransferIn size={14} />}
                >
                  Released Bids from Pool ({releasedBidCount})
                </Tabs.Tab>
                <Tabs.Tab
                  value="releasedFromPayroll"
                  icon={<IconTransferOut size={14} />}
                >
                  Released from this Payroll ({releasedFromBidCount})
                </Tabs.Tab>
                <Tabs.Tab value="allBids" icon={<IconList size={14} />}>
                  All Bids ({allBidsTotal})
                </Tabs.Tab>
                <Tabs.Tab
                  value="confirmation"
                  icon={<IconCircleCheck size={14} />}
                >
                  Confirmation
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="byWallets">
                <Accordion
                  variant="separated"
                  value={expandedWallet}
                  onChange={handleWalletAccordionChange}
                >
                  {summary.map((item, idx) => {
                    const walletBids =
                      walletBidsMap[item.walletId] ?? item.bids ?? [];
                    const itemWithBids = { ...item, bids: walletBids };
                    return (
                      <Accordion.Item
                        key={item.walletId || idx}
                        value={item.walletId}
                      >
                        <Accordion.Control>
                          <PayrollBidRowHeader
                            item={itemWithBids}
                            formatter={formatter}
                            cellRender={cellRender}
                            onHold={handleHoldByWallet}
                            loading={
                              loading || loadingWalletId === item.walletId
                            }
                            isConfirmed={isConfirmed}
                            payrollId={payrollId}
                          />
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Box pos="relative" style={{ minHeight: 60 }}>
                            <LoadingOverlay
                              visible={loadingWalletId === item.walletId}
                              overlayBlur={1}
                              loaderProps={{ size: "sm" }}
                            />
                            <PayrollBidRowContent
                              item={itemWithBids}
                              formatter={formatter}
                              payrollId={payrollId}
                            />
                          </Box>
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </Tabs.Panel>

              <Tabs.Panel value="byOrder">
                {orderSummaryLoading ? (
                  <Paper p="xl" withBorder>
                    <Stack align="center" spacing="xs" py="xl">
                      <Text size="sm" color="dimmed">
                        Loading order groups
                        {orderGroupCount > 0 ? ` (${orderGroupCount})` : ""}...
                      </Text>
                    </Stack>
                  </Paper>
                ) : orderSummary.length === 0 ? (
                  <Paper p="xl" withBorder>
                    <Stack align="center" spacing="xs" py="xl">
                      <Text size="lg" weight={500} color="dimmed">
                        {orderGroupCount > 0
                          ? "Could not load order groups"
                          : "No order bids in this payroll"}
                      </Text>
                      {orderGroupCount > 0 && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => loadOrderGroupSummary(true)}
                        >
                          Retry
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ) : (
                  <>
                    <ByOrderFilterBar
                      orderSummary={orderSummary}
                      tableFilterQuery={orderTableFilterQuery}
                      onFilterChange={onOrderTableFilterChange}
                    />
                    <Group position="apart" mb="md">
                      <Text size="sm" color="dimmed">
                        Showing {filteredOrderSummary.length} of{" "}
                        {orderSummary.length} orders
                      </Text>
                      {batchHoldAction.action && (
                        <Button
                          size="xs"
                          color={
                            batchHoldAction.action === "hold" ? "orange" : "teal"
                          }
                          leftIcon={<IconPlayerPause size={14} />}
                          onClick={handleBatchHoldClick}
                          disabled={isConfirmed || loading}
                          loading={loading}
                        >
                          {batchHoldAction.action === "hold"
                            ? `Hold All (${batchHoldAction.count})`
                            : `Unhold All (${batchHoldAction.count})`}
                        </Button>
                      )}
                    </Group>
                    {filteredOrderSummary.length === 0 ? (
                      <Paper p="xl" withBorder>
                        <Stack align="center" spacing="xs" py="xl">
                          <Text size="lg" weight={500} color="dimmed">
                            No orders match the current filters
                          </Text>
                          <Text size="sm" color="dimmed">
                            Adjust or clear filters to see order groups.
                          </Text>
                        </Stack>
                      </Paper>
                    ) : (
                      <Accordion
                        variant="separated"
                        value={expandedOrder}
                        onChange={handleOrderAccordionChange}
                      >
                        {filteredOrderSummary.map((item, idx) => (
                          <Accordion.Item
                            key={item.key || idx}
                            value={item.key}
                          >
                            <Accordion.Control>
                              <ByOrderRowHeader
                                item={{
                                  ...item,
                                  bids: orderBidsMap[item.key] || [],
                                }}
                                formatter={formatter}
                                cellRender={cellRender}
                                onHold={handleHoldBySource}
                                loading={loadingSourceKey === item.key}
                                isConfirmed={isConfirmed}
                                payrollId={payrollId}
                              />
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Box pos="relative" style={{ minHeight: 60 }}>
                                <LoadingOverlay
                                  visible={loadingSourceKey === item.key}
                                  overlayBlur={1}
                                  loaderProps={{ size: "sm" }}
                                />
                                {orderBidsMap[item.key] && (
                                  <ByOrderRowContent
                                    item={{
                                      ...item,
                                      bids: orderBidsMap[item.key],
                                    }}
                                    formatter={formatter}
                                    cellRender={cellRender}
                                    payrollId={payrollId}
                                  />
                                )}
                              </Box>
                            </Accordion.Panel>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="byIncentive">
                {!loading && incentiveSummary.length === 0 ? (
                  <Paper p="xl" withBorder>
                    <Stack align="center" spacing="xs" py="xl">
                      <IconCoins size={48} color="gray" opacity={0.5} />
                      <Text size="lg" weight={500} color="dimmed">
                        No Incentive Bids
                      </Text>
                      <Text size="sm" color="dimmed">
                        There are no commission bids from incentives in this
                        payroll.
                      </Text>
                    </Stack>
                  </Paper>
                ) : (
                  <Accordion
                    variant="separated"
                    onChange={handleIncentiveAccordionChange}
                  >
                    {incentiveSummary.map((item, idx) => (
                      <Accordion.Item key={item.key || idx} value={item.key}>
                        <Accordion.Control>
                          <ByIncentiveRowHeader
                            item={item}
                            formatter={formatter}
                            onHold={handleHoldBySource}
                            loading={loadingSourceKey === item.key}
                            isConfirmed={isConfirmed}
                          />
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Box pos="relative" style={{ minHeight: 60 }}>
                            <LoadingOverlay
                              visible={loadingSourceKey === item.key}
                              overlayBlur={1}
                              loaderProps={{ size: "sm" }}
                            />
                            {orderBidsMap[item.key] && (
                              <ByIncentiveRowContent
                                item={{
                                  ...item,
                                  bids: orderBidsMap[item.key],
                                }}
                                formatter={formatter}
                                cellRender={cellRender}
                              />
                            )}
                          </Box>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="releasedFromPool">
                <ReleasedFromPoolTabContent
                  bids={releasedBids}
                  loading={releasedLoading}
                  releasedCount={releasedBidCount}
                  releasedTotalAmount={
                    releasedTotalAmount ||
                    verification?.releasedFromHoldingPool ||
                    0
                  }
                  formatter={formatter}
                />
              </Tabs.Panel>

              <Tabs.Panel value="releasedFromPayroll">
                <ReleasedFromPoolTabContent
                  bids={releasedFromBids}
                  loading={releasedFromLoading}
                  releasedCount={releasedFromBidCount}
                  releasedTotalAmount={releasedFromTotalAmount}
                  formatter={formatter}
                  mode="from"
                />
              </Tabs.Panel>

              <Tabs.Panel value="allBids">
                {innerTab === "allBids" &&
                  bids.length === 0 &&
                  !loading &&
                  allBidsTotal === 0 &&
                  totalBids > 0 && (
                    <Text size="sm" color="dimmed">
                      Click to load bids.
                    </Text>
                  )}
                {(bids.length > 0 || (innerTab === "allBids" && loading)) && (
                  <>
                    <Table fontSize="xs" striped highlightOnHover withBorder>
                      <thead>
                        <tr>
                          <th style={{ width: 40, textAlign: "center" }}>#</th>
                          <th style={{ minWidth: 200 }}>Bid</th>
                          <th style={{ minWidth: 100 }}>Order</th>
                          <th style={{ minWidth: 100 }}>Debit Note</th>
                          <th style={{ minWidth: 130 }}>Client</th>
                          <th style={{ minWidth: 130 }}>Product</th>
                          <th style={{ minWidth: 120 }}>Wallet</th>
                          <th style={{ width: 100, textAlign: "right" }}>
                            Amount
                          </th>
                          <th style={{ width: 70, textAlign: "center" }}>
                            Hold
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bids.map((bid, index) => {
                          const rowNum =
                            (allBidsPage - 1) * allBidsLimit + index + 1;
                          const client =
                            bid.debitNote?.client || bid.order?.client;
                          const product =
                            bid.debitNote?.product ||
                            bid.debitNote?.order?.product ||
                            bid.order?.product ||
                            bid.debitNote?.productType ||
                            bid.debitNote?.order?.productType ||
                            bid.order?.productType;
                          const provider =
                            bid.debitNote?.provider ||
                            bid.debitNote?.order?.provider ||
                            bid.order?.provider;
                          const walletUser = bid.wallet?.user;
                          const walletUserGroup = bid.wallet?.userGroup;
                          const isGroupBidWallet =
                            bid.wallet?.type === "GROUP" || !!walletUserGroup;
                          const walletName = isGroupBidWallet
                            ? walletUserGroup?.name ||
                              bid.wallet?.name ||
                              bid.commissionRow?.userGroup?.name ||
                              "-"
                            : walletUser?.name || bid.wallet?.name || "-";
                          const staffId = isGroupBidWallet
                            ? null
                            : walletUser?.staffId;

                          const getStatusColor = (status) => {
                            switch (status) {
                              case "SETTLED":
                                return "green";
                              case "HOLD":
                                return "yellow";
                              case "PENDING":
                              default:
                                return "gray";
                            }
                          };

                          const getSourceLabel = () => {
                            if (bid.sourceType === "ADJUSTMENT")
                              return "Adjustment";
                            if (bid.sourceType === "COMMISSIONSTATEMENT")
                              return "Comm. Statement";
                            if (
                              bid.sourceType === "CLIENTPAYMENT" ||
                              bid.debitNote
                            )
                              return "Client Payment";
                            if (bid.order) return "Comm. Statement";
                            if (bid.sourceType === "INCENTIVE" || bid.incentive)
                              return "Incentive/Charge";
                            return bid.sourceType;
                          };

                          const getSourceColor = () => {
                            if (bid.sourceType === "ADJUSTMENT") return "red";
                            if (bid.sourceType === "COMMISSIONSTATEMENT")
                              return "violet";
                            if (
                              bid.sourceType === "CLIENTPAYMENT" ||
                              bid.debitNote
                            )
                              return "blue";
                            if (bid.order) return "violet";
                            if (bid.sourceType === "INCENTIVE" || bid.incentive)
                              return "teal";
                            return "gray";
                          };

                          return (
                            <tr key={bid._id || index}>
                              <td style={{ textAlign: "center" }}>
                                <Text size="xs" color="dimmed">
                                  {rowNum}
                                </Text>
                              </td>
                              <td>
                                <Group noWrap spacing={1}>
                                  <Badge
                                    size="xs"
                                    color={getStatusColor(bid.status)}
                                    variant="filled"
                                    radius="0"
                                  >
                                    {bid.status}
                                  </Badge>
                                  <Badge
                                    size="xs"
                                    color={getSourceColor()}
                                    variant="light"
                                    radius="0"
                                  >
                                    {getSourceLabel()}
                                  </Badge>
                                </Group>
                                <Text size="xs" weight={500}>
                                  {bid.code}
                                </Text>
                              </td>
                              <td>
                                <EntityTabAnchor
                                  entity="order"
                                  id={bid.order?._id || bid.order}
                                  label={bid.order?.code}
                                />
                              </td>
                              <td>
                                <EntityTabAnchor
                                  entity="debitNote"
                                  id={bid.debitNote?._id || bid.debitNote}
                                  label={bid.debitNote?.code}
                                />
                              </td>
                              <td>
                                {client ? (
                                  <Stack spacing={0}>
                                    <Text size="xs" weight={500} lineClamp={1}>
                                      {client.name || client.code}
                                    </Text>
                                    {client.cname && (
                                      <Text
                                        size="xs"
                                        color="dimmed"
                                        lineClamp={1}
                                      >
                                        {client.cname}
                                      </Text>
                                    )}
                                  </Stack>
                                ) : (
                                  <Text size="xs" color="dimmed">
                                    -
                                  </Text>
                                )}
                              </td>
                              <td>
                                {product || provider ? (
                                  <Stack spacing={0}>
                                    {provider && (
                                      <Text
                                        size="xs"
                                        color="blue"
                                        weight={500}
                                        lineClamp={1}
                                      >
                                        {provider.shortName ||
                                          provider.name ||
                                          provider.code}
                                      </Text>
                                    )}
                                    {product && (
                                      <Text
                                        size="xs"
                                        weight={500}
                                        lineClamp={1}
                                      >
                                        {product.name || product.code}
                                      </Text>
                                    )}
                                    {product?.cname && (
                                      <Text
                                        size="xs"
                                        color="dimmed"
                                        lineClamp={1}
                                      >
                                        {product.cname}
                                      </Text>
                                    )}
                                  </Stack>
                                ) : (
                                  <Text size="xs" color="dimmed">
                                    -
                                  </Text>
                                )}
                              </td>
                              <td>
                                <Stack spacing={0}>
                                  {staffId && (
                                    <Text size="xs" color="dimmed">
                                      {staffId}
                                    </Text>
                                  )}
                                  <Text size="xs" weight={500} lineClamp={1}>
                                    {walletName}
                                  </Text>
                                </Stack>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <Text
                                  size="xs"
                                  weight={600}
                                  color={
                                    bid.commissionAmount >= 0 ? "teal" : "red"
                                  }
                                >
                                  {formatter.currency(bid.commissionAmount)}
                                </Text>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <Tooltip
                                  label={
                                    bid.status === "HOLD"
                                      ? "Release this bid"
                                      : "Hold this bid"
                                  }
                                >
                                  <ActionIcon
                                    size="sm"
                                    variant={
                                      bid.status === "HOLD" ? "filled" : "light"
                                    }
                                    color="yellow"
                                    disabled={
                                      loading ||
                                      bid.status === "SETTLED" ||
                                      isConfirmed
                                    }
                                    onClick={() =>
                                      handleToggleBidHold(bid._id, bid.status)
                                    }
                                  >
                                    <IconPlayerPause size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    {allBidsTotal > allBidsLimit && (
                      <Group position="apart" mt="md">
                        <Text size="xs" color="dimmed">
                          Page {allBidsPage} of{" "}
                          {Math.ceil(allBidsTotal / allBidsLimit) || 1} (
                          {allBidsTotal} bids)
                        </Text>
                        <Group spacing="xs">
                          <Button
                            size="xs"
                            variant="light"
                            disabled={allBidsPage <= 1 || loading}
                            onClick={() => loadAllBidsPage(allBidsPage - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            disabled={
                              allBidsPage >=
                                Math.ceil(allBidsTotal / allBidsLimit) ||
                              loading
                            }
                            onClick={() => loadAllBidsPage(allBidsPage + 1)}
                          >
                            Next
                          </Button>
                        </Group>
                      </Group>
                    )}
                  </>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="confirmation">
                <ConfirmationTabContent
                  form={form}
                  bids={bids}
                  summary={summary}
                  loading={loading}
                  negativeBalancePreview={negativeBalancePreview}
                  onConfirm={reloadBids}
                />
              </Tabs.Panel>
            </Tabs>
          ) : null}

      {totalBids === 0 &&
        releasedBidCount === 0 &&
        releasedFromBidCount === 0 &&
        !loading && (
        <Card>
          <Title order={6}>No payroll bids found</Title>
          <Text size="xs" color="dimmed" mt="xs">
            Run Client Payments Search (and Orders Update if needed), then click
            Generate Bids. Sources already commissioned on dispatched payrolls
            are skipped automatically.
          </Text>
        </Card>
      )}

      <Modal
        opened={batchHoldModalOpened}
        onClose={closeBatchHoldModal}
        title={
          pendingBatchHold
            ? `Hold All (${batchHoldAction.count})?`
            : `Unhold All (${batchHoldAction.count})?`
        }
        centered
      >
        <Text size="sm" mb="lg">
          {pendingBatchHold
            ? `This will hold all bids for ${batchHoldAction.count} filtered order groups.`
            : `This will unhold all bids for ${batchHoldAction.count} filtered order groups.`}
        </Text>
        <Group position="right">
          <Button variant="default" onClick={closeBatchHoldModal}>
            Cancel
          </Button>
          <Button
            color={pendingBatchHold ? "orange" : "teal"}
            onClick={() => executeBatchHold(pendingBatchHold)}
            loading={loading}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default PayrollBidsTab;
